import * as _ from 'lodash'
import {BehaviorSubject} from 'rxjs'
import {IProgressProvider} from '../PA.Core/PA.Core.IF.Basic';
import {ASync} from '../PA.Core/Basic/ASync'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {PAdbContext} from '../PA.Core/PAdbContext';
import {
    esWB_Product_Group, esWB_Customer, esWB_Product_Condition
} from "../PA.Core/PAdbSchema";

import {
    WB_CustomerRow, WB_ProductConditionRow,
    WB_ProductGroupRow,
} from '../PA.Core/PAdbSchemaRows';
import {IHaveConditions, IHaveConditionsListenerFunction} from "../PA.MD.Core/PA.MD.IF.Aspects";

import {PASearch} from "../PA.Core/PASearch";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";


export interface ConditionCollectionOptions {
    dbContext?: PAdbContext,
    ConditionCreateOptions?: any,
    ConditionLoadOptions?: any
}

const ConditionCollectionOptions_default : ConditionCollectionOptions = {
    ConditionCreateOptions: {bEagerLoadAspectInterfaces: false},
    ConditionLoadOptions: {}
}


export class ConditionCollection
    implements
        IHaveConditions
{
    _dbContext: PAdbContext
    _options: ConditionCollectionOptions

    _bIHaveConditionsLoaded: boolean;
    _OnIHaveConditionsLoaded: Promise<IHaveConditions>;

    _prodCndMID: number

    _dctPrdGrpRowQueuedById: { [id: number]: WB_ProductGroupRow }
    _dctPrdGrpRowLoadedById: { [id: number]: WB_ProductGroupRow }
    _bLoadAllPrdGrpsQueued: boolean
    _bLoadAllPrdGrpsLoaded: boolean

    _dctCustomerRowQueuedById: { [id: number]: WB_CustomerRow }
    _dctCustomerRowLoadedById: { [id: number]: WB_CustomerRow }
    _bLoadAllCustomersQueued: boolean
    _bLoadAllCustomersLoaded: boolean

    _bQueueLoadProcessRunning: boolean
    _bQueueLoadProcessPaused: boolean

    _dctConditionById: { [id: number]: WB_ProductConditionRow }
    _listeners: IHaveConditionsListenerFunction[]
    _ConditionsChanged: BehaviorSubject<IHaveConditions>;
    _ConditionLoadProgressProvider: IProgressProvider;

    _QueueChanged: BehaviorSubject<IHaveConditions>;

    constructor(options?: ConditionCollectionOptions) {

        this._options = {}
        _.defaults(this._options, options || {}, ConditionCollectionOptions_default)

        if ('dbContext' in this._options)
            this._dbContext = this._options.dbContext;
        else
            this._dbContext = undefined

        this._bIHaveConditionsLoaded = false
        this._OnIHaveConditionsLoaded = undefined

        this._dctPrdGrpRowQueuedById = {}
        this._dctPrdGrpRowLoadedById = {}
        this._bLoadAllPrdGrpsQueued = false
        this._bLoadAllPrdGrpsLoaded = false

        this._dctCustomerRowQueuedById = {}
        this._dctCustomerRowLoadedById = {}
        this._bLoadAllCustomersQueued = false
        this._bLoadAllCustomersLoaded = false

        this._bQueueLoadProcessRunning = false
        this._bQueueLoadProcessPaused = false

        this._dctConditionById = {}
        this._listeners = []
        this._ConditionsChanged = new BehaviorSubject<IHaveConditions>(this)
        this._ConditionLoadProgressProvider = new ProgressProvider("Condition Loader", this)

        this._QueueChanged = new BehaviorSubject<IHaveConditions>(this)
    }

    get dbContext() {
        return this._dbContext
    }
    set dbContext(value: PAdbContext) {
        // any validations.. ie.. no events can be loaded..
        this._dbContext = value
    }

    get ProductConditionTypeId() {
        return this._prodCndMID
    }
    set ProductConditionTypeId(value: number) {
        // any validations.. ie.. no events can be loaded..
        this._prodCndMID = value
    }

    //<editor-fold desc="IHaveConditions operations">

    // Initial Loading
    get OnIHaveConditionsLoaded(): Promise<IHaveConditions> {
        let self = this
        //
        if (this._bIHaveConditionsLoaded) {
            return ASync.resolve(this)
        }

        if (!_.isEmpty(self._OnIHaveConditionsLoaded)) {
            return self._OnIHaveConditionsLoaded
        }

        self._OnIHaveConditionsLoaded = new Promise<IHaveConditions>((resolve, error) => {
            resolve(self)
        })

        return self._OnIHaveConditionsLoaded
    }
    get bIHaveConditionsLoaded(): boolean {
        return this._bIHaveConditionsLoaded
    }

    // Event Load Operations
    pauseLoadProcessing(): void {
        this._bQueueLoadProcessPaused = true
    }
    resumeLoadProcessing(): void {
        this._bQueueLoadProcessPaused = false
        this._IHaveConditions_processQueue()
    }

    //Condition Operations
    hasCondition(aPROD_CND_ID: number): boolean {
        return ( aPROD_CND_ID in this._dctConditionById)
    }

    getCondition(aPROD_CND_ID: number): WB_ProductConditionRow {
        return this._dctConditionById[aPROD_CND_ID];
    }


    get Conditions(): WB_ProductConditionRow[] {
        return _.values(this._dctConditionById)
    }

    get ConditionsChanged(): BehaviorSubject<IHaveConditions> {
        return this._ConditionsChanged
    }


    addChangeListener(aListener: IHaveConditionsListenerFunction): void {

        if (_.isEmpty(aListener)) {
            return
        }

        let found = false
        _.forEach(this._listeners, (element) => {
            if (element == aListener) {
                found = true
                return false
            }
        })

        if (!found) {
            this._listeners.push(aListener)
        }
    }
    removeChangeListener(aListener: IHaveConditionsListenerFunction): void {
        _.remove(this._listeners, (element) => {
            return (element == aListener)
        })
    }


    // Load by Product Groups
    loadProductGroups(arrWBPGIds: number[]): void {
        let self = this

        let loadPromise = undefined
        if (_.isEmpty(arrWBPGIds)) {
            loadPromise = ASync.resolve([])
        } else if (_.isArray(arrWBPGIds)) {
            loadPromise = self.dbContext.findUsingCache(esWB_Product_Group, arrWBPGIds)
        } else {
            // Argument not array or empty
            return
        }

        loadPromise
            .then((arrWB_PrdGrp_Rows: WB_ProductGroupRow[]) => {

                if (_.isEmpty(arrWB_PrdGrp_Rows)) {
                    return
                }

                _.forEach(arrWB_PrdGrp_Rows, (aWBPrdGrpRow: WB_ProductGroupRow)=> {
                    if(aWBPrdGrpRow.cif.WB_PRDGRP_ID in  self._dctPrdGrpRowQueuedById) {
                        // Don't re-add it.
                    } else {
                        self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
                    }
                })
                self._notifyQueueChange()
                self._IHaveConditions_processQueue()

            })
            .catch((err) => {
                console.log(err)
                return
            })
    }

    loadAllProductGroups(): void {
        this._bLoadAllPrdGrpsQueued = true
        this._notifyQueueChange()
        this._IHaveConditions_processQueue()
    }
    get ProductGroupsLoaded(): WB_ProductGroupRow[] {
        return _.values(this._dctPrdGrpRowLoadedById)
    }
    get ProductGroupsAllLoaded(): boolean {
        return this._bLoadAllPrdGrpsLoaded
    }


    // Load by Customers
    loadCustomers(arrWBCustomerIds: number[]): void {
        let self = this

        let loadPromise = undefined
        if (_.isEmpty(arrWBCustomerIds)) {
            loadPromise = ASync.resolve([])
        } else if (_.isArray(arrWBCustomerIds)) {
            loadPromise = self.dbContext.findUsingCache(esWB_Customer, arrWBCustomerIds)
        } else {
            // Argument not array or empty
            return
        }

        loadPromise
            .then((arrWB_Customer_Rows: WB_CustomerRow[]) => {

                if (_.isEmpty(arrWB_Customer_Rows)) { return }

                _.forEach(arrWB_Customer_Rows, (aWBCustomerRow: WB_CustomerRow)=> {
                    if(aWBCustomerRow.cif.WB_CUST_ID in  self._dctCustomerRowQueuedById) {
                        // Don't re-add it.
                    } else {
                        self._dctCustomerRowQueuedById[aWBCustomerRow.cif.WB_CUST_ID] = aWBCustomerRow
                    }
                })
                self._notifyQueueChange()
                self._IHaveConditions_processQueue()

            })
            .catch((err) => {
                console.log(err)
                return
            })
    }

    loadAllCustomers(): void {
        this._bLoadAllCustomersQueued = true
        this._notifyQueueChange()
        this._IHaveConditions_processQueue()
    }
    get CustomersLoaded(): WB_CustomerRow[] {
        return _.values(this._dctCustomerRowLoadedById)
    }
    get CustomersAllLoaded(): boolean {
        return this._bLoadAllCustomersLoaded
    }

    get ConditionLoadProgressProvider(): IProgressProvider { return this._ConditionLoadProgressProvider }


    private _IHaveConditions_processQueue(): void {

        let self = this
        let bDebugQueueProcessing = true
        let bLoadIteratively = true

        // 0. If Loading paused, exit
        // 1. If Load Running, exit
        // 2. Check if the queue is empty
        // 3. Copy the queue, union with already loaded
        // 4. Determine PROD_CND_ID set load set from union of all queued & loaded
        // 5. Difference Loaded Conditions to Determined Load List
        // 6. Load Conditions && Add them to Collection
        // 7. Delete loaded items from the queue, Update Loaded List
        // 8. Notify addition

        if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue started')

        // 0. If Loading Paused, exit
        if (self._bQueueLoadProcessPaused) {
            console.log('IHaveConditions_processQueue ended: paused')
            return
        }

        // 1. If Load Running, exit
        if (self._bQueueLoadProcessRunning) {
            console.log('IHaveConditions_processQueue ended: already running')
            return
        }

        // 2. Check if the queue is empty
        if (_.isEmpty(self._dctPrdGrpRowQueuedById)
            && (self._bLoadAllPrdGrpsQueued == self._bLoadAllPrdGrpsLoaded)
            && _.isEmpty(self._dctCustomerRowQueuedById)
            && (self._bLoadAllCustomersQueued == self._bLoadAllCustomersLoaded)
        ) {
            console.log('IHaveConditions_processQueue ended: empty queue')
            return
        }

        // Mark running
        self._bQueueLoadProcessRunning = true
        if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue set Queue Running.')
        self._ConditionLoadProgressProvider.start("Loading Conditions")

        let failCount = 0
        let successCount  = 0
        let addedConditionsRows: WB_ProductConditionRow[] = []


        // 3. Copy the queue, union with already loaded
        let dctPrdGrpRowByIdCurrentQueueLoad = _.clone(self._dctPrdGrpRowQueuedById)
        let bLoadAllProductGroupCurrentQueueLoad = self._bLoadAllPrdGrpsQueued || self._bLoadAllPrdGrpsLoaded
        let dctCustomerRowByIdCurrentQueueLoad = _.clone(self._dctCustomerRowQueuedById)
        let bLoadAllCustomerCurrentQueueLoad = self._bLoadAllCustomersQueued || self._bLoadAllCustomersLoaded

        _.forEach(self._dctPrdGrpRowLoadedById, (aPrdGrpRow: WB_ProductGroupRow) => {
            if (!(aPrdGrpRow.cif.WB_PRDGRP_ID in dctPrdGrpRowByIdCurrentQueueLoad)) {
                dctPrdGrpRowByIdCurrentQueueLoad[aPrdGrpRow.cif.WB_PRDGRP_ID] = aPrdGrpRow
            }
        })
        _.forEach(self._dctCustomerRowLoadedById, (aCustomerRow: WB_CustomerRow) => {
            if (!(aCustomerRow.cif.WB_CUST_ID in dctCustomerRowByIdCurrentQueueLoad)) {
                dctCustomerRowByIdCurrentQueueLoad[aCustomerRow.cif.WB_CUST_ID] = aCustomerRow
            }
        })

        let arrWBPrdGrpIds = _.map(dctPrdGrpRowByIdCurrentQueueLoad,(aPrdGrpRow: WB_ProductGroupRow) => {
            return aPrdGrpRow.cif.WB_PRDGRP_ID
        })

        let arrWBCustomerIds = _.map(dctCustomerRowByIdCurrentQueueLoad,(aCustomerRow: WB_CustomerRow) => {
            return aCustomerRow.cif.WB_CUST_ID
        })

        let condition: any = {PROD_CNDM_ID: self._prodCndMID}
        let whereFieldsJSON: any = {PROD_CNDM_ID: {type: 'number'}}
        if (!bLoadAllProductGroupCurrentQueueLoad) {
            if (!_.isEmpty(arrWBPrdGrpIds)) {
                condition = {and: [condition, {WB_PRDGRP_ID: {inq: arrWBPrdGrpIds}}]}
                whereFieldsJSON.WB_PRDGRP_ID = {type: 'number'}
            }
        }
        if (!bLoadAllCustomerCurrentQueueLoad) {
            if (!_.isEmpty(arrWBCustomerIds)) {
                condition = {and: [condition, {WB_CUST_ID: {inq: arrWBCustomerIds}}]}
                whereFieldsJSON.WB_CUST_ID = {type: 'number'}
            }
        }

        if(bDebugQueueProcessing) console.log('Queue Condition: ' + JSON.stringify(condition))
        PASearch.find('vUI_ProductCondition_Search',
            {where: condition},
            whereFieldsJSON,
            [],
            0,
            [])
            .then((arrWBConditionsFound: any[]) => {
                if (!_.isArray(arrWBConditionsFound)){
                    if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue set Queue Running = false, not array.')
                    self._bQueueLoadProcessRunning = false
                    self._ConditionLoadProgressProvider.finish()
                    return
                }

                let arrWBConditionToLoad: {PROD_CND_ID: number}[] = []

                // 5. Difference Loaded Conditions to Determined Load List
                _.forEach(arrWBConditionsFound, (aRow) => {
                    if (!(aRow.PROD_CND_ID in self._dctConditionById)) {
                        arrWBConditionToLoad.push(aRow)
                    }
                })

                if (_.isEmpty(arrWBConditionToLoad)){
                    if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue set Queue Running = false, already loaded.')
                    self._bQueueLoadProcessRunning = false
                    self._ConditionLoadProgressProvider.finish()
                    return
                }

                self._ConditionLoadProgressProvider.setProgressDenominator(arrWBConditionToLoad.length)

                // 6. Load Conditions && Add them to Collection
                let arrConditionIdsToLoad = _.map(arrWBConditionToLoad, (aRow) => { return aRow.PROD_CND_ID })

                const product_condition_options: CreateOptions = {
                    standardConditions: {
                    },
                    dctIncludeBelongsToNamedRelations: {
                    }
                }

                const aRecResolver = new ESRecordResolver(self._dbContext, product_condition_options)
                return aRecResolver.findAllWithRelationships(esWB_Product_Condition,arrConditionIdsToLoad)
            })
            .then((arrConditionRows: WB_ProductConditionRow[]) => {
                    _.forEach(arrConditionRows, (aConditionRow) => {

                        // Load BOs into Collection

                            if (!(aConditionRow.cif.PROD_CND_ID in self._dctConditionById)) {
                                addedConditionsRows.push(aConditionRow)
                                self._dctConditionById[aConditionRow.cif.PROD_CND_ID] = aConditionRow
                            }
                    })

                    self._ConditionLoadProgressProvider.setProgressNumerator(successCount + failCount)


                if ((successCount == 0) && (failCount > 0)) {
                    if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue set Queue Running = false, loader failed.')
                    self._bQueueLoadProcessRunning = false
                    self._ConditionLoadProgressProvider.finish()
                    return
                }

                // 7. Delete loaded items from the queue, Update Loaded List
                // dctPrdGrpRowByIdCurrentQueueLoad
                // bLoadAllProductGroupCurrentQueueLoad
                // dctCustomerRowByIdCurrentQueueLoad
                // bLoadAllPCustomerCurrentQueueLoad

                _.forEach(dctPrdGrpRowByIdCurrentQueueLoad, (aWBPrdGrpRow: WB_ProductGroupRow) => {
                    // Remove PrdGrps from Queue
                    if (aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowQueuedById) {
                        delete self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID]
                    }

                    // Add to Loaded
                    if (!(aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowLoadedById)) {
                        self._dctPrdGrpRowLoadedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
                    }
                })

                _.forEach(dctCustomerRowByIdCurrentQueueLoad, (aWBCustomerRow: WB_CustomerRow) => {
                    // Remove Customers from Queue
                    if (aWBCustomerRow.cif.WB_CUST_ID in self._dctCustomerRowQueuedById) {
                        delete self._dctCustomerRowQueuedById[aWBCustomerRow.cif.WB_CUST_ID]
                    }

                    // Add to Loaded
                    if (!(aWBCustomerRow.cif.WB_CUST_ID in self._dctCustomerRowLoadedById)) {
                        self._dctCustomerRowLoadedById[aWBCustomerRow.cif.WB_CUST_ID] = aWBCustomerRow
                    }
                })

                self._bLoadAllPrdGrpsLoaded = bLoadAllProductGroupCurrentQueueLoad
                self._bLoadAllCustomersLoaded = bLoadAllCustomerCurrentQueueLoad

                if(bDebugQueueProcessing) console.log('IHaveConditions_processQueue set Queue Running = false, done.')
                self._bQueueLoadProcessRunning = false

                self._ConditionLoadProgressProvider.finish()


                self._notifyQueueChange()

                // 8. Notify addition
                if (!bLoadIteratively) {
                    self._notifyChange('add', addedConditionsRows, {})
                }

                setTimeout(function(){
                    self._IHaveConditions_processQueue()
                }, 0)

            })
            .catch((err) => {
                console.log('IHaveConditions_processQueue set Queue Running = false, error.')
                self._bQueueLoadProcessRunning = false
                self._ConditionLoadProgressProvider.exception(err)
            })

    }


    // Queue Functions
    get QueueChanged(): BehaviorSubject<IHaveConditions> {
        return this._QueueChanged
    }

    getProductGroupAll(bIncludeQueued?: boolean): boolean {
        if(bIncludeQueued){
            return this._bLoadAllPrdGrpsQueued || this._bLoadAllPrdGrpsLoaded
        } else {
            return this._bLoadAllPrdGrpsLoaded
        }
    }

    getProductGroups(bIncludeQueued?: boolean): WB_ProductGroupRow[] {
        if(bIncludeQueued){
            return _.union(_.values(this._dctPrdGrpRowQueuedById),_.values(this._dctPrdGrpRowLoadedById))
        } else {
            return _.values(this._dctPrdGrpRowLoadedById)
        }
    }

    getCustomerAll(bIncludeQueued?: boolean): boolean {
        if(bIncludeQueued){
            return this._bLoadAllCustomersQueued || this._bLoadAllCustomersLoaded
        } else {
            return this._bLoadAllCustomersLoaded
        }
    }

    getCustomers(bIncludeQueued?: boolean): WB_CustomerRow[] {
        if(bIncludeQueued){
            return _.union(_.values(this._dctCustomerRowQueuedById),_.values(this._dctCustomerRowLoadedById))
        } else {
            return _.values(this._dctCustomerRowLoadedById)
        }
    }



    private _notifyChange(type: string, conditionRows: WB_ProductConditionRow[], opts: any) {
        _.forEach(this._listeners, (aListener: IHaveConditionsListenerFunction) => {
            if (aListener) {
                aListener(this, type, conditionRows, opts)
            }
        })
        this._ConditionsChanged.next(this)
    }

    private _notifyQueueChange() {
        this._QueueChanged.next(this)
    }


//</editor-fold>

}

